import React, { useState } from 'react';
import axios from 'axios';

const AddPopupModal = ({ onClose, onAdd }) => {
  const [newPopup, setNewPopup] = useState({
    image: null,
    imageFile: null,
    title: '',
    content: '',
    catchphrase: '',
    isActive: true,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_API;


  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewPopup({ ...newPopup, image: reader.result, imageFile: file });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!newPopup.imageFile || !newPopup.title.trim() || !newPopup.content.trim()) {
      setError('Please fill in all required fields.');
      return;
    }

    setIsLoading(true);

    try {
      const formData = new FormData();
      
      // Prepare the popup data object
      const popupData = {
        title: newPopup.title,
        description: newPopup.content,
        catchphrase: newPopup.catchphrase,
        isActive: newPopup.isActive
      };

      // Add the image file
      formData.append('image', newPopup.imageFile);
      // Add the popup data as JSON string
      formData.append('pop_up', JSON.stringify(popupData));

      const response = await axios.post(
        `${API_BASE_URL}/add-popup`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      // Call the parent's onAdd with the response data
      onAdd({
        id: response.data.id,
        title: popupData.title,
        content: popupData.description,
        catchphrase: popupData.catchphrase,
        image: response.data.image,
        isActive: popupData.isActive
      });

      // Close the modal on success
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create popup');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">Create New Popup</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            disabled={isLoading}
          >
            &times;
          </button>
        </div>

        {error && (
          <div className="mb-4 p-2 bg-red-100 text-red-700 rounded-md text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Upload Image *
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              required
              disabled={isLoading}
            />
            {newPopup.image && (
              <div className="mt-2">
                <img 
                  src={newPopup.image} 
                  alt="Preview" 
                  className="w-full h-40 object-cover rounded-lg" 
                />
              </div>
            )}
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title *
            </label>
            <input
              type="text"
              value={newPopup.title}
              onChange={(e) => setNewPopup({ ...newPopup, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              placeholder="Popup title"
              required
              disabled={isLoading}
            />
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Content *
            </label>
            <textarea
              value={newPopup.content}
              onChange={(e) => setNewPopup({ ...newPopup, content: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              placeholder="Popup message content"
              rows="3"
              required
              disabled={isLoading}
            ></textarea>
          </div>

          {/* Catchphrase */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Catchphrase
            </label>
            <input
              type="text"
              value={newPopup.catchphrase}
              onChange={(e) => setNewPopup({ ...newPopup, catchphrase: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              placeholder="e.g., Limited Time!"
              disabled={isLoading}
            />
          </div>

          {/* Active Checkbox */}
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={newPopup.isActive}
              onChange={(e) => setNewPopup({ ...newPopup, isActive: e.target.checked })}
              id="popup-active"
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              disabled={isLoading}
            />
            <label htmlFor="popup-active" className="ml-2 block text-sm text-gray-700">
              Active
            </label>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center justify-center"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating...
                </>
              ) : (
                'Create Popup'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddPopupModal;